import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Group the integer part of a numeric draft with thousands separators while
// keeping a single decimal portion (and a trailing "." mid-typing). Anything
// non-numeric the user pastes is dropped; the commit path re-parses anyway.
function groupThousands(raw: string): string {
  // Keep a trailing unit suffix — K/M/B/T, %, " Days" — so the value the field
  // DISPLAYS while idle can be typed straight back into it. Stripping it here
  // meant no parser could ever see it: "42.1B" was already "42.1" by the time
  // blur ran, and a $42.1B cap committed as $42.10.
  const cut = raw.search(/[A-Za-z%]/);
  const suffix = cut === -1 ? '' : raw.slice(cut);
  const s = (cut === -1 ? raw : raw.slice(0, cut)).replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  const intPart = dot === -1 ? s : s.slice(0, dot);
  const fracPart = dot === -1 ? null : s.slice(dot + 1).replace(/\./g, '');
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const grouped =
    fracPart === null ? groupedInt : `${groupedInt}.${fracPart}`;
  return grouped + suffix;
}

function countDigits(s: string): number {
  let c = 0;
  for (let i = 0; i < s.length; i++) if (s[i] >= '0' && s[i] <= '9') c++;
  return c;
}

// Caret index in `formatted` sitting just after `digitsBefore` digits — lets us
// keep the cursor stable across reformatting (separators shift char offsets).
function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      seen++;
      if (seen === digitsBefore) return i + 1;
    }
  }
  return formatted.length;
}

interface PillInputProps {
  label: string;
  value: number;
  placeholder: string;
  onCommit: (num: number) => void;
  /**
   * Turns the typed string into a number. Without it the field falls back to
   * `parseFloat`, which reads only the leading digits — so "42.1B", copied
   * from the very display this field shows when idle, committed 42.1.
   */
  parseInput?: (s: string) => number | null;
  formatDisplay: (v: number) => string;
  isButton?: boolean;
  buttonLabel?: string;
  onButtonClick?: () => void;
  /** When provided, a hover "x" clears/removes this bound (Figma min/max). */
  onClear?: () => void;
}

export function PillInput({
  label,
  value,
  placeholder,
  onCommit,
  parseInput,
  formatDisplay,
  isButton = false,
  buttonLabel,
  onButtonClick,
  onClear,
}: PillInputProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Caret to restore after a reformat-driven re-render (set in onChange).
  const caretRef = useRef<number | null>(null);

  // Never surface more than 2 decimals in the field: a seeded p90 cap or a
  // hydrated bound can arrive as a long raw float (e.g. 483.5799865722656).
  const displayValue = Math.round(value * 100) / 100;

  useEffect(() => {
    if (!editing) setDraft(groupThousands(String(displayValue)));
  }, [displayValue, editing]);

  useLayoutEffect(() => {
    if (caretRef.current != null && inputRef.current) {
      const pos = caretRef.current;
      caretRef.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    }
  });

  const handleFocus = () => {
    // A button that seeds a value (e.g. "+ Max" → the 90th-percentile cap)
    // applies it and stays a filled field; it must NOT drop into an edit box
    // with a stale draft, or blur would commit the pre-seed value back.
    if (isButton && onButtonClick) {
      onButtonClick();
      return;
    }
    // A button with no seed opens an EMPTY editor: its underlying value is a
    // sentinel (an uncapped max is Number.MAX_SAFE_INTEGER), which must never
    // be shown as a real number to type over.
    setDraft(isButton ? '' : groupThousands(String(displayValue)));
    setEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const raw = el.value;
    const caret = el.selectionStart ?? raw.length;
    const digitsBefore = countDigits(raw.slice(0, caret));
    const formatted = groupThousands(raw);
    caretRef.current = caretAfterDigits(formatted, digitsBefore);
    setDraft(formatted);
  };

  const handleBlur = () => {
    setEditing(false);
    const stripped = draft.replace(/,/g, '');
    // Opening a field is not a decision. Committing an untouched draft wrote
    // the bound into the strategy the moment "+ Min" was clicked, so the
    // filter read as configured before anything had been typed.
    if (stripped === String(displayValue)) return;
    // The caller's parser knows the unit suffixes its own formatter writes;
    // parseFloat is the fallback for fields that don't supply one.
    const parsed = parseInput ? parseInput(stripped) : parseFloat(stripped);
    const num = parsed == null ? NaN : parsed;
    if (!isNaN(num)) {
      onCommit(Math.round(num * 100) / 100);
    } else {
      setDraft(String(displayValue));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      setDraft(String(displayValue));
      setEditing(false);
      e.currentTarget.blur();
    }
  };

  const hasValue = formatDisplay(value) !== '' && !isButton;

  return (
    <div className="flex-1 space-y-1.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div
        className={cn(
          'relative group flex items-center justify-center h-9 rounded-md border bg-background text-sm transition-colors',
          'focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/20',
          isButton
            ? 'border-dashed border-primary/30'
            : hasValue
              ? 'border-primary/30'
              : 'border-border',
        )}
      >
        {isButton && !editing ? (
          <button
            type="button"
            className="flex items-center gap-1 text-muted-foreground w-full h-full justify-center"
            onClick={handleFocus}
          >
            <span className="text-primary">+</span> {buttonLabel}
          </button>
        ) : (
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={editing ? draft : formatDisplay(displayValue)}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'w-full h-full bg-transparent text-center text-sm outline-none',
              // Extra right room so a long value doesn't sit under the clear "x".
              onClear ? 'px-7' : 'px-4',
            )}
          />
        )}
        {/* Per-field "x" — clears/removes this bound, reverting to the
            "+ Min" / "+ Max" add button (Figma UniverseSelection.tsx:3965).
            Hover-revealed; hidden while editing or when there's no value. */}
        {onClear && hasValue && !editing && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            aria-label={`Clear ${label}`}
            className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/60 opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
